"use client";

import React from "react";
import { motion } from "framer-motion";

import Image from "next/image";
import Link from "next/link";
import { PortableText } from "next-sanity";
import { customRichTextComponent } from "@/sanity/sanityPortableText";
import { GroqImage } from "@/helpers/Image";
import { Check } from "lucide-react";
import { CTAButton } from "@/shadcn/ui/button";
import Video from "@/components/global/Video";
import EventCountdown from "@/components/event/CustomCountdown";

import deco1 from "@/public/krypto-day/blur.png";
import deco2 from "@/public/krypto-day/blur2.png";
import noise from "@/public/krypto-day/Noise & Texture.png";
import squares from "@/public/krypto-day/squares.png";
import ellipse from "@/public/krypto-day/ellipse.svg";
import logo from "@/public/frankfurt-event/logo.svg";

import { PageKryptoDaySchema } from "@/sanity/pages/PageKryptoDayTemplate";

// Shared easing curve (matches Home Hero)

export default function KryptoDayHero({ data }: { data: PageKryptoDaySchema }) {
  return (
    <section className="relative pt-[12rem] pb-[6rem] overflow-clip lg:pt-[18rem] lg:pb-[10rem]">
      {/* Header / Nav */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="absolute left-0 w-full z-[99] top-[3rem] lg:top-[4rem]"
      >
        <div className="w-[min(179rem,100%)] lg:px-0 px-[2rem] sm:mx-auto flex items-center justify-between pb-[2rem] lg:pb-0">
          <Link href="/">
            <Image className="!w-[15.375rem]" src={logo} alt="logo" />
          </Link>

          <div className="py-[1rem] px-[1.5rem] rounded-[0.75rem] bg-[#FFFFFF]/[15%] lg:bg-[#261313] border border-white/[15%] lg:border-none backdrop-blur-[2rem]">
            <p className="mb-0 text-white text-[2rem] font-[500] lg:text-[#FFB6B6]">
              {data?.headerChip}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Top decorative light bar */}
      <div
        className="w-[min(43.25rem,100%)] h-[0.375rem] bg-gradient-to-r from-white/0 via-white/60 to-white/0 absolute top-0 left-1/2 -translate-x-1/2 z-[1]"
        style={{
          boxShadow:
            "0 0 41px 0 rgba(255, 255, 255, 0.56), 0 0 128px 0 rgba(69, 229, 180, 0.50)",
        }}
      />

      <div className="w-[min(120rem,100%)] h-[0.375rem] bg-gradient-to-r from-[#4EC9A3]/0 via-[#4EC9A3]/60 to-[#4EC9A3]/0 absolute top-0 left-1/2 -translate-x-1/2 z-[1]" />

      {/* Background decorations */}
      <Image
        src={deco1}
        alt=""
        className="absolute top-0 left-1/2 opacity-60 -translate-x-1/2 mix-blend-screen z-[2] lg:scale-100 scale-[300%]"
      />
      <Image
        src={deco2}
        alt=""
        className="absolute top-0 left-1/2 opacity-60 -translate-x-1/2 mix-blend-screen z-[2] lg:scale-100 scale-[300%]"
      />
      <Image
        src={noise}
        alt=""
        className="object-cover absolute top-0 w-full opacity-10 z-[3] mix-blend-soft-light"
      />
      <Image
        src={squares}
        alt=""
        className="hidden object-cover absolute top-0 w-full opacity-60 lg:block z-[2] mix-blend-overlay"
      />
      <Image
        src={ellipse}
        alt=""
        className="object-cover absolute left-1/2 scale-125 -translate-x-1/2 lg:scale-100 z-[2] top-[10rem] lg:top-[4rem]"
      />

      {/* Hero content */}
      <div className="relative px-[2rem] w-[min(179.25rem,100%)] mx-auto flex flex-col items-center text-center z-[9]">
        {/* Logos - fade in + slide up */}
        {data?.heroLogos && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            <GroqImage
              src={data.heroLogos}
              alt="logos"
              className="object-cover w-[27rem]"
            />
          </motion.div>
        )}

        {/* Title - fade in + slide up with stagger */}
        {data?.heroTitles && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.7,
              delay: 0.35,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="w-[min(100rem,100%)] mb-[2rem] richtext richtext-center prose-p:text-white/70 prose-p:text-[2rem] prose-p:w-[min(47.375rem,100%)] prose-h1:!text-[9rem] prose-h1:font-[600] prose-h1:bg-gradient-to-b prose-h1:from-[#FFFFFF] prose-h1:to-[#999999] prose-h1:bg-clip-text prose-h1:text-transparent"
          >
            <PortableText
              value={data?.heroTitles}
              components={customRichTextComponent({})}
            />
          </motion.div>
        )}

        {/* CTA - fade in + slide up */}
        {data?.heroCTA && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="[&_*]:font-sans [&_*]:uppercase"
          >
            <CTAButton data={data?.heroCTA} className="" />
          </motion.div>
        )}

        {/* Video + items + countdown area */}
        <div className="flex flex-col w-full mt-[7.5rem] gap-[2rem]">
          <div className="flex flex-col-reverse w-full lg:flex-col gap-[2rem]">
            {/* Hero check items - staggered fade in */}
            {data?.heroItems && data.heroItems.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.5,
                  delay: 0.6,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="flex flex-col justify-between lg:flex-row lg:items-center gap-[1rem] lg:gap-[2rem]"
              >
                {data.heroItems.map((item, index) => (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.4,
                      delay: 0.7 + index * 0.1,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    className="flex !items-center gap-[1rem]"
                    key={index}
                  >
                    <div className="shrink-0 w-[2rem] h-[2rem] lg:w-[3rem] lg:h-[3rem]">
                      <Check color="#B9F641" className="w-full h-full" />
                    </div>
                    <p className="m-0 text-white/70 text-[2rem]">{item}</p>
                  </motion.div>
                ))}
              </motion.div>
            )}

            {/* Video - 3D perspective reveal (matching Home Hero platform card) */}
            <div className="w-full relative [&>div]:h-full [&_video]:!object-cover aspect-video">
              <motion.div
                initial={{ opacity: 0, rotateX: 50, scale: 0.95 }}
                animate={{ opacity: 1, rotateX: 0, scale: 1 }}
                transition={{
                  duration: 1,
                  delay: 0.3,
                  ease: [0.22, 1, 0.36, 1],
                }}
                style={{ transformOrigin: "bottom" }}
                className="w-full h-full"
              >
                <Video videoData={data?.heroVideo} />
              </motion.div>
            </div>
          </div>

          {/* Countdown - fade in + slide up */}
          {data?.countdown && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.7,
                delay: 0.8,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="relative w-full bg-gradient-to-b border-white lg:p-0 from-[#D2FA83] to-[#E8FEBD] rounded-[1.5rem] p-[2rem] lg:h-[16.75rem]"
            >
              <div className="flex flex-col items-center lg:absolute lg:top-1/2 lg:left-1/2 lg:flex-row lg:-translate-x-1/2 lg:-translate-y-1/2 gap-[2rem] lg:gap-[5rem]">
                <div className="!shrink-0">
                  <p className="text-black text-center font-sans text-[2.5rem] font-[600] leading-[130%] !flex-nowrap m-0">
                    {data?.videoCalloutText}
                  </p>
                </div>

                <div className="shrink-0">
                  <EventCountdown dateTimeString={data?.countdown} />
                </div>

                {data?.videoCalloutCTA && (
                  <div className="[&_button]:bg-black [&_button]:text-white hover:[&_button]:text-black lg:w-fit lg:[&_button]:w-fit w-full [&_*]:font-sans [&_*]:uppercase">
                    <CTAButton data={data?.videoCalloutCTA} className="" />
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
